import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pill, StickyNote, X, Plus, Send, Trash2, Activity, Video, AlertTriangle, CalendarPlus, ArrowRightLeft, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Ruler,
  Weight,
  ShoppingCart,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  RotateCcw,
  FileText,
  Search,
} from "lucide-react";
import { format } from "date-fns";

interface PatientDetailDrawerProps {
  patientId: string | null;
  onClose: () => void;
}

interface FullProfile {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email?: string;
  phone: string | null;
  date_of_birth: string | null;
  sex: string | null;
  height: string | null;
  weight: number | null;
  address_line1: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  assigned_provider_id: string | null;
  referred_by_salesperson: string | null;
  last_checkup_date: string | null;
  last_sign_in_at: string | null;
  created_at: string;
}

interface PatientOrder {
  id: string;
  order_type: string;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
}

interface Assessment {
  id: string;
  status: string;
  goal: string | null;
  peptide_interest: string | null;
  created_at: string;
}

interface Prescription {
  id: string;
  peptide_name: string;
  dosage: string;
  frequency: string;
  route: string;
  quantity: string;
  refills: number;
  status: string;
  sent_at: string | null;
  created_at: string;
}

interface PatientNote {
  id: string;
  note: string;
  tags: string[];
  author_id: string;
  created_at: string;
}

interface PatientAppointment {
  id: string;
  start_time: string;
  end_time: string;
  status: string;
  call_type: string;
  provider_id: string;
  notes: string | null;
  provider_name?: string;
}

interface StatusChangeEvent {
  id: string;
  appointment_id: string;
  previous_status: string | null;
  new_status: string;
  changed_by: string | null;
  created_at: string;
  changer_name?: string;
}

const statusIcon: Record<string, any> = {
  pending: Clock,
  active: CheckCircle,
  fulfilled: Package,
  cancelled: XCircle,
  refunded: RotateCcw,
};

const statusVariant: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  pending: "secondary",
  active: "default",
  fulfilled: "outline",
  cancelled: "destructive",
  refunded: "destructive",
};

const SUGGESTED_TAGS = ["VIP", "Follow-up needed", "Compliance concern", "New patient", "Flagged", "Responsive"];

const TIMELINE_COLORS: Record<string, string> = {
  assessment: "bg-blue-500",
  order: "bg-emerald-500",
  prescription: "bg-purple-500",
  note: "bg-amber-500",
  consultation: "bg-teal-500",
  status_change: "bg-orange-500",
  account: "bg-muted-foreground",
};

const TIMELINE_ICONS: Record<string, any> = {
  assessment: FileText,
  order: ShoppingCart,
  prescription: Pill,
  note: StickyNote,
  consultation: Video,
  status_change: ArrowRightLeft,
  account: User,
};

interface TimelineEvent {
  id: string;
  type: "assessment" | "order" | "prescription" | "note" | "consultation" | "status_change" | "account";
  title: string;
  description?: string;
  date: Date;
  status?: string;
}

const TIMELINE_FILTER_OPTIONS: { type: TimelineEvent["type"]; label: string }[] = [
  { type: "assessment", label: "Assessments" },
  { type: "order", label: "Orders" },
  { type: "prescription", label: "Prescriptions" },
  { type: "consultation", label: "Consultations" },
  { type: "status_change", label: "Status Changes" },
  { type: "note", label: "Notes" },
];

const ActivityTimeline = ({
  assessments,
  orders,
  prescriptions,
  notes,
  appointments,
  statusChanges,
  createdAt,
}: {
  assessments: Assessment[];
  orders: PatientOrder[];
  prescriptions: Prescription[];
  notes: PatientNote[];
  appointments: PatientAppointment[];
  statusChanges: StatusChangeEvent[];
  createdAt: string;
}) => {
  const [activeFilters, setActiveFilters] = useState<Set<TimelineEvent["type"]>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  const toggleFilter = (type: TimelineEvent["type"]) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };

  const allEvents: TimelineEvent[] = [
    { id: "account-created", type: "account" as const, title: "Account created", date: new Date(createdAt) },
    ...assessments.map((a) => ({
      id: `a-${a.id}`,
      type: "assessment" as const,
      title: a.peptide_interest || a.goal || "Health Assessment",
      status: a.status,
      date: new Date(a.created_at),
    })),
    ...orders.map((o) => ({
      id: `o-${o.id}`,
      type: "order" as const,
      title: `${o.order_type} order — $${(o.amount_cents / 100).toFixed(2)}`,
      status: o.status,
      date: new Date(o.created_at),
    })),
    ...prescriptions.map((rx) => ({
      id: `rx-${rx.id}`,
      type: "prescription" as const,
      title: rx.peptide_name,
      description: `${rx.dosage} · ${rx.frequency}`,
      status: rx.status,
      date: new Date(rx.created_at),
    })),
    ...notes.map((n) => ({
      id: `n-${n.id}`,
      type: "note" as const,
      title: n.note.length > 80 ? n.note.slice(0, 80) + "…" : n.note,
      description: n.tags?.length ? n.tags.join(", ") : undefined,
      date: new Date(n.created_at),
    })),
    ...appointments.map((appt) => ({
      id: `appt-${appt.id}`,
      type: "consultation" as const,
      title: `${appt.call_type} consultation — ${appt.provider_name || "Unknown"}`,
      description: appt.notes || undefined,
      status: appt.status,
      date: new Date(appt.start_time),
    })),
    ...statusChanges.map((sc) => ({
      id: `sc-${sc.id}`,
      type: "status_change" as const,
      title: sc.previous_status
        ? `Consultation ${sc.previous_status} → ${sc.new_status}`
        : `Consultation created as ${sc.new_status}`,
      description: sc.changer_name ? `by ${sc.changer_name}` : undefined,
      date: new Date(sc.created_at),
      status: sc.new_status,
    })),
  ].sort((a, b) => b.date.getTime() - a.date.getTime());

  const query = searchQuery.toLowerCase();
  const events = allEvents.filter((e) => {
    const matchesType = activeFilters.size === 0 || e.type === "account" || activeFilters.has(e.type);
    const matchesSearch = !query ||
      e.title.toLowerCase().includes(query) ||
      (e.description || "").toLowerCase().includes(query) ||
      (e.status || "").toLowerCase().includes(query) ||
      e.type.toLowerCase().includes(query);
    return matchesType && matchesSearch;
  });

  if (allEvents.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No activity yet</p>;
  }

  return (
    <div className="space-y-2">
      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search events…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8 h-8 text-xs"
        />
      </div>
      {/* Filter chips */}
      <div className="flex flex-wrap gap-1.5">
        {TIMELINE_FILTER_OPTIONS.map(({ type, label }) => {
          const active = activeFilters.has(type);
          const color = TIMELINE_COLORS[type];
          return (
            <button
              key={type}
              onClick={() => toggleFilter(type)}
              className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                active
                  ? "border-primary bg-primary/10 text-primary font-medium"
                  : "border-border text-muted-foreground hover:bg-secondary"
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${color}`} />
              {label}
            </button>
          );
        })}
        {activeFilters.size > 0 && (
          <button
            onClick={() => setActiveFilters(new Set())}
            className="text-[11px] px-2 py-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* Timeline */}
      <div className="relative space-y-0">
        {events.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No matching events</p>
        ) : (
          events.map((event, idx) => {
            const Icon = TIMELINE_ICONS[event.type] || Clock;
            const dotColor = TIMELINE_COLORS[event.type] || "bg-muted-foreground";
            const isLast = idx === events.length - 1;
            return (
              <div key={event.id} className="relative flex gap-3 pb-4">
                {!isLast && (
                  <div className="absolute left-[11px] top-6 bottom-0 w-px bg-border" />
                )}
                <div className={`relative z-10 mt-1 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full ${dotColor}`}>
                  <Icon size={11} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-foreground leading-tight truncate">{event.title}</p>
                    {event.status && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0 capitalize">
                        {event.status}
                      </Badge>
                    )}
                  </div>
                  {event.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{event.description}</p>
                  )}
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {format(event.date, "MMM d, yyyy · h:mm a")}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const BookConsultationButton = ({
  patientId,
  profile,
  providerName,
  assessments,
  onClose,
}: {
  patientId: string;
  profile: FullProfile | null;
  providerName: string | null;
  assessments: Assessment[];
  onClose: () => void;
}) => {
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleClick = async () => {
    if (state === "sending" || state === "sent") return;
    setState("sending");
    const approvedAssessment = assessments.find((a) => a.status === "approved" || a.status === "reviewed");
    try {
      await supabase.functions.invoke('send-transactional-email', {
        body: {
          templateName: 'schedule-consultation-reminder',
          recipientUserId: patientId,
          idempotencyKey: `consult-reminder-${patientId}-${Date.now()}`,
          notificationCategory: 'appointment_reminders',
          templateData: {
            patientName: profile?.first_name || undefined,
            providerName: providerName || undefined,
            goal: approvedAssessment?.goal || approvedAssessment?.peptide_interest || undefined,
          },
        },
      });
      setState("sent");
      toast.success("Consultation reminder email sent to patient");
      // Navigate after a brief delay so user sees the success state
      setTimeout(() => {
        onClose();
        window.location.href = `/book?patient=${patientId}`;
      }, 1500);
    } catch (e) {
      console.error("Failed to send reminder email", e);
      setState("error");
      toast.error("Failed to send reminder email");
      // Reset after a moment so they can retry
      setTimeout(() => setState("idle"), 2000);
    }
  };

  return (
    <Button
      size="sm"
      className={`gap-1.5 shrink-0 transition-all duration-300 ${
        state === "sent"
          ? "bg-emerald-600 hover:bg-emerald-600 text-white"
          : state === "error"
          ? "bg-destructive hover:bg-destructive text-destructive-foreground"
          : ""
      }`}
      disabled={state === "sending" || state === "sent"}
      onClick={handleClick}
    >
      {state === "idle" && <><CalendarPlus size={14} /> Book Consultation</>}
      {state === "sending" && <><Loader2 size={14} className="animate-spin" /> Sending Reminder…</>}
      {state === "sent" && <><CheckCircle2 size={14} /> Email Sent ✓</>}
      {state === "error" && <><AlertTriangle size={14} /> Failed — Retry</>}
    </Button>
  );
};

const PatientDetailDrawer = ({ patientId, onClose }: PatientDetailDrawerProps) => {
  const [profile, setProfile] = useState<FullProfile | null>(null);
  const [orders, setOrders] = useState<PatientOrder[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [notes, setNotes] = useState<PatientNote[]>([]);
  const [appointments, setAppointments] = useState<PatientAppointment[]>([]);
  const [statusChanges, setStatusChanges] = useState<StatusChangeEvent[]>([]);
  const [providerName, setProviderName] = useState<string | null>(null);
  const [salespeople, setSalespeople] = useState<{ user_id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  // Note form state
  const [newNote, setNewNote] = useState("");
  const [newTags, setNewTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{ appointmentId: string; oldStatus: string; newStatus: string } | null>(null);
  const [cancelReason, setCancelReason] = useState("");

  // Fetch salespeople once
  useEffect(() => {
    const fetchSp = async () => {
      const { data: roles } = await supabase.from("user_roles").select("user_id").eq("role", "salesperson" as any);
      if (roles && roles.length > 0) {
        const ids = roles.map((r: any) => r.user_id);
        const { data: profiles } = await supabase.from("profiles").select("user_id, first_name, last_name").in("user_id", ids);
        setSalespeople((profiles || []).map((p: any) => ({
          user_id: p.user_id,
          name: `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.user_id.slice(0, 8),
        })));
      }
    };
    fetchSp();
  }, []);
  useEffect(() => {
    if (!patientId) return;
    setLoading(true);

    const fetchAll = async () => {
      const [profileRes, ordersRes, assessmentsRes, prescriptionsRes, notesRes, emailRes, appointmentsRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("user_id, first_name, last_name, phone, date_of_birth, sex, height, weight, address_line1, city, state, zip_code, assigned_provider_id, referred_by_salesperson, last_checkup_date, last_sign_in_at, created_at")
          .eq("user_id", patientId)
          .single(),
        supabase
          .from("orders")
          .select("id, order_type, status, amount_cents, currency, created_at")
          .eq("patient_id", patientId)
          .order("created_at", { ascending: false }),
        supabase
          .from("health_assessments")
          .select("id, status, goal, peptide_interest, created_at")
          .eq("user_id", patientId)
          .order("created_at", { ascending: false }),
        supabase
          .from("prescriptions")
          .select("id, peptide_name, dosage, frequency, route, quantity, refills, status, sent_at, created_at")
          .eq("patient_id", patientId)
          .order("created_at", { ascending: false }),
        supabase
          .from("patient_notes")
          .select("id, note, tags, author_id, created_at")
          .eq("patient_id", patientId)
          .order("created_at", { ascending: false }),
        supabase.rpc("get_user_emails"),
        supabase
          .from("appointments")
          .select("id, start_time, end_time, status, call_type, provider_id, notes")
          .eq("patient_id", patientId)
          .order("start_time", { ascending: false }),
      ]);

      const emailMap: Record<string, string> = {};
      ((emailRes.data as any[]) || []).forEach((e: { user_id: string; email: string }) => {
        emailMap[e.user_id] = e.email;
      });

      if (profileRes.data) {
        setProfile({ ...profileRes.data, email: emailMap[patientId] || undefined } as FullProfile & { email?: string });

        if (profileRes.data.assigned_provider_id) {
          const { data: prov } = await supabase
            .from("profiles")
            .select("first_name, last_name")
            .eq("user_id", profileRes.data.assigned_provider_id)
            .single();
          setProviderName(prov ? `Dr. ${prov.last_name || prov.first_name || "Unknown"}` : null);
        } else {
          setProviderName(null);
        }
      }

      // Resolve provider names for appointments
      const rawAppts = (appointmentsRes.data || []) as PatientAppointment[];
      const apptProviderIds = [...new Set(rawAppts.map((a) => a.provider_id))];
      const providerNameMap: Record<string, string> = {};
      if (apptProviderIds.length > 0) {
        const { data: provProfiles } = await supabase
          .from("profiles")
          .select("user_id, first_name, last_name")
          .in("user_id", apptProviderIds);
        (provProfiles || []).forEach((p) => {
          providerNameMap[p.user_id] = `Dr. ${p.last_name || p.first_name || "Unknown"}`;
        });
      }
      const enrichedAppts = rawAppts.map((a) => ({ ...a, provider_name: providerNameMap[a.provider_id] || "Unknown" }));

      // Fetch appointment status history
      const apptIds = rawAppts.map((a) => a.id);
      let enrichedStatusChanges: StatusChangeEvent[] = [];
      if (apptIds.length > 0) {
        const { data: scData } = await supabase
          .from("appointment_status_history")
          .select("id, appointment_id, previous_status, new_status, changed_by, created_at")
          .in("appointment_id", apptIds)
          .order("created_at", { ascending: false });
        const rawSc = (scData || []) as StatusChangeEvent[];
        // Resolve changer names
        const changerIds = [...new Set(rawSc.map((s) => s.changed_by).filter(Boolean))] as string[];
        const changerNameMap: Record<string, string> = {};
        if (changerIds.length > 0) {
          const { data: changerProfiles } = await supabase
            .from("profiles")
            .select("user_id, first_name, last_name")
            .in("user_id", changerIds);
          (changerProfiles || []).forEach((p) => {
            changerNameMap[p.user_id] = `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Unknown";
          });
        }
        enrichedStatusChanges = rawSc.map((s) => ({
          ...s,
          changer_name: s.changed_by ? changerNameMap[s.changed_by] || undefined : undefined,
        }));
      }

      setOrders((ordersRes.data as PatientOrder[]) || []);
      setAssessments((assessmentsRes.data as Assessment[]) || []);
      setPrescriptions((prescriptionsRes.data as Prescription[]) || []);
      setNotes((notesRes.data as PatientNote[]) || []);
      setAppointments(enrichedAppts);
      setStatusChanges(enrichedStatusChanges);
      setLoading(false);
    };

    fetchAll();
  }, [patientId]);

  const handleAddNote = useCallback(async () => {
    if (!patientId || !newNote.trim()) return;
    setSubmitting(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSubmitting(false); return; }

    const { data, error } = await supabase
      .from("patient_notes")
      .insert({ patient_id: patientId, author_id: user.id, note: newNote.trim(), tags: newTags })
      .select("id, note, tags, author_id, created_at")
      .single();

    if (error) {
      toast.error("Failed to add note");
    } else if (data) {
      setNotes((prev) => [data as PatientNote, ...prev]);
      setNewNote("");
      setNewTags([]);
      setTagInput("");
      setShowNoteForm(false);
      toast.success("Note added");
    }
    setSubmitting(false);
  }, [patientId, newNote, newTags]);

  const handleDeleteNote = useCallback(async (noteId: string) => {
    const { error } = await supabase.from("patient_notes").delete().eq("id", noteId);
    if (error) {
      toast.error("Failed to delete note");
    } else {
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success("Note deleted");
    }
  }, []);

  const addTag = (tag: string) => {
    const t = tag.trim();
    if (t && !newTags.includes(t)) setNewTags((prev) => [...prev, t]);
    setTagInput("");
  };

  const handleChangeAppointmentStatus = useCallback(async (appointmentId: string, oldStatus: string, newStatus: string, reason?: string) => {
    if (oldStatus === newStatus) return;
    const trimmedReason = reason?.trim();
    const existingAppt = appointments.find((a) => a.id === appointmentId);
    const updatePayload: { status: string; notes?: string } = { status: newStatus };
    if (newStatus === "cancelled" && trimmedReason) {
      const stamp = new Date().toISOString();
      const reasonLine = `[Cancelled ${stamp}] Reason: ${trimmedReason}`;
      updatePayload.notes = existingAppt?.notes ? `${existingAppt.notes}\n\n${reasonLine}` : reasonLine;
    }
    const { error } = await supabase
      .from("appointments")
      .update(updatePayload)
      .eq("id", appointmentId);
    if (error) {
      toast.error("Failed to update status");
      return;
    }
    // Update local state
    setAppointments((prev) =>
      prev.map((a) => (a.id === appointmentId ? { ...a, status: newStatus } : a))
    );
    // Refetch status changes for timeline
    const { data: scData } = await supabase
      .from("appointment_status_history")
      .select("id, appointment_id, previous_status, new_status, changed_by, created_at")
      .in("appointment_id", appointments.map((a) => a.id))
      .order("created_at", { ascending: false });
    if (scData) {
      const changerIds = [...new Set(scData.map((s: any) => s.changed_by).filter(Boolean))] as string[];
      const changerNameMap: Record<string, string> = {};
      if (changerIds.length > 0) {
        const { data: changerProfiles } = await supabase
          .from("profiles")
          .select("user_id, first_name, last_name")
          .in("user_id", changerIds);
        (changerProfiles || []).forEach((p) => {
          changerNameMap[p.user_id] = `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Unknown";
        });
      }
      setStatusChanges(scData.map((s: any) => ({
        ...s,
        changer_name: s.changed_by ? changerNameMap[s.changed_by] || undefined : undefined,
      })));
    }
    toast.success(`Status changed to ${newStatus.replace("_", " ")}`);

    // Insert notification and send email when admin marks as completed
    if (newStatus === "completed" && patientId) {
      await supabase.from("notifications").insert({
        user_id: patientId,
        title: "Consultation Completed",
        message: "Your consultation has been completed. Your provider will follow up with next steps regarding your treatment plan.",
        type: "consultation_completed",
        related_id: appointmentId,
      });

      const appt = appointments.find((a) => a.id === appointmentId);
      try {
        const { data: patientEmails } = await supabase.rpc("get_user_emails");
        const patientEmail = patientEmails?.find((e: any) => e.user_id === patientId)?.email;
        if (patientEmail && appt) {
          const providerDisplayName = appt.provider_name || "Your provider";
          const recipientName = profile?.first_name || undefined;
          const appointmentDate = appt.start_time ? format(new Date(appt.start_time), "MMMM d, yyyy") : undefined;
          const appointmentTime = appt.start_time && appt.end_time
            ? `${format(new Date(appt.start_time), "h:mm a")} – ${format(new Date(appt.end_time), "h:mm a")}`
            : undefined;

          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "consultation-completed",
              recipientEmail: patientEmail,
              idempotencyKey: `consultation-completed-admin-${appointmentId}`,
              templateData: {
                recipientName,
                providerName: providerDisplayName,
                appointmentDate,
                appointmentTime,
              },
            },
          });
        }
      } catch (e) {
        console.error("Failed to send consultation-completed email from admin", e);
      }
    } else if (newStatus === "cancelled" && patientId) {
      await supabase.from("notifications").insert({
        user_id: patientId,
        title: "Appointment Cancelled",
        message: trimmedReason
          ? `Your scheduled consultation has been cancelled. Reason: ${trimmedReason}`
          : "Your scheduled consultation has been cancelled. Please contact our care team if you have any questions.",
        type: "appointment_cancelled",
        related_id: appointmentId,
      });

      const appt = appointments.find((a) => a.id === appointmentId);
      try {
        const { data: patientEmails } = await supabase.rpc("get_user_emails");
        const patientEmail = patientEmails?.find((e: any) => e.user_id === patientId)?.email;
        if (patientEmail && appt) {
          const providerDisplayName = appt.provider_name || "Your provider";
          const recipientName = profile?.first_name || undefined;
          const appointmentDate = appt.start_time ? format(new Date(appt.start_time), "MMMM d, yyyy") : undefined;
          const appointmentTime = appt.start_time && appt.end_time
            ? `${format(new Date(appt.start_time), "h:mm a")} – ${format(new Date(appt.end_time), "h:mm a")}`
            : undefined;

          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "appointment-cancelled",
              recipientEmail: patientEmail,
              idempotencyKey: `appt-cancelled-admin-${appointmentId}`,
              templateData: {
                recipientName,
                providerName: providerDisplayName,
                appointmentDate,
                appointmentTime,
                cancelReason: trimmedReason,
              },
            },
          });
        }
      } catch (e) {
        console.error("Failed to send appointment-cancelled email from admin", e);
      }
    } else if (newStatus === "rescheduled" && patientId) {
      await supabase.from("notifications").insert({
        user_id: patientId,
        title: "Appointment Rescheduled",
        message: "Your consultation has been rescheduled. Please check your dashboard for updated appointment details.",
        type: "appointment_rescheduled",
        related_id: appointmentId,
      });

      const appt = appointments.find((a) => a.id === appointmentId);
      try {
        const { data: patientEmails } = await supabase.rpc("get_user_emails");
        const patientEmail = patientEmails?.find((e: any) => e.user_id === patientId)?.email;
        if (patientEmail && appt) {
          const providerDisplayName = appt.provider_name || "Your provider";
          const recipientName = profile?.first_name || undefined;
          const appointmentDate = appt.start_time ? format(new Date(appt.start_time), "MMMM d, yyyy") : undefined;
          const appointmentTime = appt.start_time && appt.end_time
            ? `${format(new Date(appt.start_time), "h:mm a")} – ${format(new Date(appt.end_time), "h:mm a")}`
            : undefined;

          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "appointment-rescheduled",
              recipientEmail: patientEmail,
              idempotencyKey: `appt-rescheduled-admin-${appointmentId}`,
              templateData: {
                recipientName,
                providerName: providerDisplayName,
                appointmentDate,
                appointmentTime,
              },
            },
          });
        }
      } catch (e) {
        console.error("Failed to send appointment-rescheduled email from admin", e);
      }
    }
  }, [appointments, patientId, profile]);

  const patientName = profile
    ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || "Unnamed Patient"
    : "";

  const formatAmount = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const totalSpent = orders
    .filter((o) => o.status !== "cancelled" && o.status !== "refunded")
    .reduce((sum, o) => sum + o.amount_cents, 0);

  return (
    <>
    <Sheet open={!!patientId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : profile ? (
          <>
            <SheetHeader>
              <SheetTitle className="font-display text-xl flex items-center gap-2">
                <User size={18} className="text-primary" />
                {patientName}
              </SheetTitle>
            </SheetHeader>

            {/* Overdue consultation banner */}
            {(() => {
              const hasApprovedAssessment = assessments.some((a) => a.status === "approved" || a.status === "reviewed");
              const hasBookedConsultation = appointments.some((a) => ["scheduled", "confirmed", "completed"].includes(a.status));
              if (hasApprovedAssessment && !hasBookedConsultation) {
                return (
                  <div className="mt-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                    <AlertTriangle size={18} className="text-amber-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-amber-900">Consultation Overdue</p>
                      <p className="text-xs text-amber-700">Assessment approved but no consultation booked yet.</p>
                    </div>
                    <BookConsultationButton
                      patientId={patientId!}
                      profile={profile}
                      providerName={providerName}
                      assessments={assessments}
                      onClose={onClose}
                    />
                  </div>
                );
              }
              return null;
            })()}

            <div className="mt-6 space-y-6">
              {/* Contact Info */}
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Contact</h3>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <InfoRow icon={Mail} label="Email" value={profile.email || "—"} />
                  <InfoRow icon={Phone} label="Phone" value={profile.phone || "—"} />
                  {(profile.address_line1 || profile.city) && (
                    <InfoRow
                      icon={MapPin}
                      label="Address"
                      value={[profile.address_line1, profile.city, profile.state, profile.zip_code].filter(Boolean).join(", ") || "—"}
                    />
                  )}
                </div>
              </section>

              <Separator />

              {/* Demographics */}
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Demographics</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <InfoRow icon={Calendar} label="DOB" value={profile.date_of_birth ? format(new Date(profile.date_of_birth + "T00:00:00"), "MMM d, yyyy") : "—"} />
                  <InfoRow icon={User} label="Sex" value={profile.sex || "—"} />
                  <InfoRow icon={Ruler} label="Height" value={profile.height || "—"} />
                  <InfoRow icon={Weight} label="Weight" value={profile.weight != null ? `${profile.weight} lbs` : "—"} />
                </div>
              </section>

              <Separator />

              {/* Care Info */}
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Care</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <InfoRow icon={User} label="Provider" value={providerName || "Unassigned"} />
                  <InfoRow icon={Calendar} label="Last Checkup" value={profile.last_checkup_date ? format(new Date(profile.last_checkup_date + "T00:00:00"), "MMM d, yyyy") : "—"} />
                  <InfoRow icon={Clock} label="Last Login" value={profile.last_sign_in_at ? format(new Date(profile.last_sign_in_at), "MMM d, yyyy h:mm a") : "Never"} />
                  <InfoRow icon={Calendar} label="Joined" value={format(new Date(profile.created_at), "MMM d, yyyy")} />
                </div>
              </section>

              <Separator />

              {/* Sales Attribution */}
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Sales Attribution</h3>
                <div className="flex items-center gap-3">
                  <Select
                    value={profile.referred_by_salesperson || "none"}
                    onValueChange={async (val) => {
                      const newVal = val === "none" ? null : val;
                      const { error } = await supabase
                        .from("profiles")
                        .update({ referred_by_salesperson: newVal })
                        .eq("user_id", profile.user_id);
                      if (error) {
                        toast.error("Failed to update salesperson");
                      } else {
                        setProfile((prev) => prev ? { ...prev, referred_by_salesperson: newVal } : null);
                        toast.success(newVal ? "Salesperson assigned — future orders will auto-attribute" : "Salesperson unassigned");
                      }
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Assign salesperson…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {salespeople.map((sp) => (
                        <SelectItem key={sp.user_id} value={sp.user_id}>{sp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground">
                  All future orders from this patient will be attributed to this salesperson.
                </p>
              </section>

              {/* Consultations */}
              <Separator />
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Video size={14} /> Consultations ({appointments.length})
                </h3>
                {appointments.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No consultations yet</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Date & Time</TableHead>
                        <TableHead className="text-xs">Provider</TableHead>
                        <TableHead className="text-xs">Type</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {appointments.map((appt) => (
                        <>
                          <TableRow key={appt.id}>
                            <TableCell className="text-xs">
                              <div>{format(new Date(appt.start_time), "MMM d, yyyy")}</div>
                              <div className="text-muted-foreground">
                                {format(new Date(appt.start_time), "h:mm a")} – {format(new Date(appt.end_time), "h:mm a")}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs">{appt.provider_name}</TableCell>
                            <TableCell className="text-xs capitalize">{appt.call_type}</TableCell>
                            <TableCell>
                              {(() => {
                                const statusColors: Record<string, string> = {
                                  scheduled: "border-blue-300 bg-blue-50 text-blue-800",
                                  confirmed: "border-emerald-300 bg-emerald-50 text-emerald-800",
                                  completed: "border-green-300 bg-green-50 text-green-800",
                                  rescheduled: "border-amber-300 bg-amber-50 text-amber-800",
                                  cancelled: "border-red-300 bg-red-50 text-red-800",
                                  no_show: "border-gray-300 bg-gray-100 text-gray-800",
                                };
                                const dotColors: Record<string, string> = {
                                  scheduled: "bg-blue-500",
                                  confirmed: "bg-emerald-500",
                                  completed: "bg-green-500",
                                  rescheduled: "bg-amber-500",
                                  cancelled: "bg-red-500",
                                  no_show: "bg-gray-500",
                                };
                                return (
                                  <Select
                                    value={appt.status}
                                    onValueChange={(value) => {
                                      if (value !== appt.status) {
                                        setPendingStatusChange({ appointmentId: appt.id, oldStatus: appt.status, newStatus: value });
                                      }
                                    }}
                                  >
                                    <SelectTrigger className={`h-7 w-[130px] text-xs capitalize border ${statusColors[appt.status] || ""}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {["scheduled", "confirmed", "completed", "rescheduled", "cancelled", "no_show"].map((s) => (
                                        <SelectItem key={s} value={s} className="text-xs capitalize">
                                          <span className="flex items-center gap-1.5">
                                            <span className={`inline-block w-2 h-2 rounded-full ${dotColors[s] || "bg-gray-400"}`} />
                                            {s.replace("_", " ")}
                                          </span>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                          {appt.notes && (
                            <TableRow key={`${appt.id}-notes`} className="border-b-0 hover:bg-transparent">
                              <TableCell colSpan={4} className="pt-0 pb-2 pl-6">
                                <div className="flex items-start gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded px-2.5 py-1.5">
                                  <FileText size={12} className="shrink-0 mt-0.5" />
                                  <span>{appt.notes}</span>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </section>

              {/* Activity Timeline */}
              <Separator />
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Activity size={14} /> Activity Timeline
                </h3>
                <ActivityTimeline
                  assessments={assessments}
                  orders={orders}
                  prescriptions={prescriptions}
                  notes={notes}
                  appointments={appointments}
                  statusChanges={statusChanges}
                  createdAt={profile.created_at}
                />
              </section>

              {/* Assessments */}
              {assessments.length > 0 && (
                <>
                  <Separator />
                  <section className="space-y-3">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <FileText size={14} /> Assessments ({assessments.length})
                    </h3>
                    <div className="space-y-2">
                      {assessments.map((a) => (
                        <div key={a.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                          <div>
                            <span className="font-medium">{a.peptide_interest || a.goal || "Health Assessment"}</span>
                            <p className="text-xs text-muted-foreground">{format(new Date(a.created_at), "MMM d, yyyy")}</p>
                          </div>
                          <Badge variant={a.status === "approved" ? "default" : a.status === "pending" ? "secondary" : "outline"} className="text-xs capitalize">
                            {a.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </section>
                </>
              )}

              {/* Orders */}
              <Separator />
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <ShoppingCart size={14} /> Orders ({orders.length})
                  </h3>
                  {totalSpent > 0 && (
                    <span className="text-sm font-semibold text-primary">
                      Total: {formatAmount(totalSpent)}
                    </span>
                  )}
                </div>

                {orders.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No orders yet</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Type</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="text-xs">Amount</TableHead>
                        <TableHead className="text-xs">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((o) => {
                        const Icon = statusIcon[o.status] || Clock;
                        return (
                          <TableRow key={o.id}>
                            <TableCell className="text-xs capitalize">{o.order_type}</TableCell>
                            <TableCell>
                              <Badge variant={statusVariant[o.status] || "secondary"} className="text-xs gap-1">
                                <Icon size={10} /> {o.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs font-medium">{formatAmount(o.amount_cents)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{format(new Date(o.created_at), "MMM d, yyyy")}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </section>

              {/* Prescriptions */}
              <Separator />
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Pill size={14} /> Prescriptions ({prescriptions.length})
                </h3>

                {prescriptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No prescriptions yet</p>
                ) : (
                  <div className="space-y-2">
                    {prescriptions.map((rx) => (
                      <div key={rx.id} className="rounded-md border border-border px-3 py-2.5 text-sm space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{rx.peptide_name}</span>
                          <Badge
                            variant={rx.status === "sent" ? "default" : rx.status === "draft" ? "secondary" : "outline"}
                            className="text-xs capitalize"
                          >
                            {rx.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {rx.dosage} · {rx.frequency} · {rx.route}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Qty: {rx.quantity} · Refills: {rx.refills}
                          {rx.sent_at && ` · Sent: ${format(new Date(rx.sent_at), "MMM d, yyyy")}`}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Created: {format(new Date(rx.created_at), "MMM d, yyyy")}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Admin Notes */}
              <Separator />
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <StickyNote size={14} /> Notes ({notes.length})
                  </h3>
                  {!showNoteForm && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setShowNoteForm(true)}>
                      <Plus size={12} /> Add Note
                    </Button>
                  )}
                </div>

                {showNoteForm && (
                  <div className="rounded-md border border-border p-3 space-y-3 bg-secondary/20">
                    <Textarea
                      placeholder="Write a note about this patient..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="text-sm min-h-[60px] resize-none"
                      maxLength={2000}
                    />
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-1.5">
                        {newTags.map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs gap-1 cursor-pointer" onClick={() => setNewTags((prev) => prev.filter((t) => t !== tag))}>
                            {tag} <X size={10} />
                          </Badge>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Add a tag..."
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(tagInput); } }}
                          className="text-xs h-7"
                        />
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {SUGGESTED_TAGS.filter((t) => !newTags.includes(t)).map((t) => (
                          <button key={t} type="button" onClick={() => addTag(t)} className="text-[10px] px-2 py-0.5 rounded-full border border-border text-muted-foreground hover:bg-secondary transition-colors">
                            + {t}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setShowNoteForm(false); setNewNote(""); setNewTags([]); setTagInput(""); }}>
                        Cancel
                      </Button>
                      <Button size="sm" className="h-7 text-xs gap-1" onClick={handleAddNote} disabled={submitting || !newNote.trim()}>
                        <Send size={10} /> Save Note
                      </Button>
                    </div>
                  </div>
                )}

                {notes.length === 0 && !showNoteForm ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No notes yet</p>
                ) : (
                  <div className="space-y-2">
                    {notes.map((n) => (
                      <div key={n.id} className="rounded-md border border-border px-3 py-2.5 text-sm space-y-1.5 group">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-foreground whitespace-pre-wrap">{n.note}</p>
                          <button
                            onClick={() => handleDeleteNote(n.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                        {n.tags && n.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {n.tags.map((tag) => (
                              <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                        <p className="text-[10px] text-muted-foreground">
                          {format(new Date(n.created_at), "MMM d, yyyy h:mm a")}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          </>
        ) : (
          <p className="text-center text-muted-foreground py-12">Patient not found</p>
        )}
      </SheetContent>
    </Sheet>

    <AlertDialog open={!!pendingStatusChange} onOpenChange={(open) => { if (!open) { setPendingStatusChange(null); setCancelReason(""); } }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {pendingStatusChange?.newStatus === "rescheduled"
              ? "Reschedule this appointment?"
              : pendingStatusChange?.newStatus === "cancelled"
              ? "Cancel this appointment?"
              : "Change appointment status?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {pendingStatusChange?.newStatus === "rescheduled" ? (
              <>
                This will cancel the patient's current appointment
                {(() => {
                  const appt = appointments.find((a) => a.id === pendingStatusChange?.appointmentId);
                  return appt ? (
                    <> scheduled for{" "}
                      <span className="font-medium">
                        {format(new Date(appt.start_time), "MMM d, yyyy 'at' h:mm a")}
                      </span>
                    </>
                  ) : null;
                })()}
                . The patient will need to book a new time. A notification will be sent to the patient.
              </>
            ) : pendingStatusChange?.newStatus === "cancelled" ? (
              <>
                This will cancel the patient's appointment
                {(() => {
                  const appt = appointments.find((a) => a.id === pendingStatusChange?.appointmentId);
                  return appt ? (
                    <> scheduled for{" "}
                      <span className="font-medium">
                        {format(new Date(appt.start_time), "MMM d, yyyy 'at' h:mm a")}
                      </span>
                    </>
                  ) : null;
                })()}
                . The patient will be notified by email and notification. This action cannot be undone.
              </>
            ) : (
              <>
                This will change the status from{" "}
                <span className="font-medium capitalize">{pendingStatusChange?.oldStatus?.replace("_", " ")}</span> to{" "}
                <span className="font-medium capitalize">{pendingStatusChange?.newStatus?.replace("_", " ")}</span>.
                This action will be logged in the activity timeline.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {pendingStatusChange?.newStatus === "cancelled" && (
          <div className="space-y-2">
            <Label htmlFor="admin-cancel-reason" className="text-sm">Reason for cancellation <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea
              id="admin-cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value.slice(0, 500))}
              placeholder="e.g. Provider unavailable, patient request, scheduling conflict…"
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">Shared with the patient in their cancellation email and saved to the appointment record.</p>
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>
            {pendingStatusChange?.newStatus === "rescheduled"
              ? "Keep Current Appointment"
              : pendingStatusChange?.newStatus === "cancelled"
              ? "Keep Appointment"
              : "Cancel"}
          </AlertDialogCancel>
          <AlertDialogAction
            className={pendingStatusChange?.newStatus === "cancelled" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : undefined}
            onClick={() => {
              if (pendingStatusChange) {
                handleChangeAppointmentStatus(pendingStatusChange.appointmentId, pendingStatusChange.oldStatus, pendingStatusChange.newStatus, cancelReason);
                setPendingStatusChange(null);
                setCancelReason("");
              }
            }}
          >
            {pendingStatusChange?.newStatus === "rescheduled"
              ? "Reschedule"
              : pendingStatusChange?.newStatus === "cancelled"
              ? "Cancel Appointment"
              : "Confirm"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

const InfoRow = ({ icon: Icon, label, value }: { icon: any; label: string; value: string }) => (
  <div className="flex items-start gap-2">
    <Icon size={14} className="text-muted-foreground mt-0.5 shrink-0" />
    <div>
      <span className="text-muted-foreground">{label}:</span>{" "}
      <span className="text-foreground">{value}</span>
    </div>
  </div>
);

export default PatientDetailDrawer;
